import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Check, Truck, Loader2, RotateCcw } from "lucide-react";
import { CardShowcase } from "@/components/CardShowcase";

const US_STATES = ["AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY","DC"];

const WHY_CARRY = [
  "Easy to keep with you",
  "Simple to give to someone",
  "QR code takes them directly to I Need a Proverb",
  "I Need a Proverb is free to use",
];

const QTY_TIERS = [
  { quantity: 100, cents: 3495 },
  { quantity: 200, cents: 4995 },
  { quantity: 500, cents: 7995 },
];
const priceStr = (c) => `$${(c / 100).toFixed(2)}`;

export function ShareCards() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [info, setInfo] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [checkoutUrl, setCheckoutUrl] = useState("");
  const initialQty = [100, 200, 500].includes(Number(params.get("qty"))) ? Number(params.get("qty")) : 100;
  const [qty, setQty] = useState(initialQty);
  const [f, setF] = useState({ firstName: "", lastName: "", email: "", phone: "", organization: "", addressLine1: "", addressLine2: "", city: "", state: "", postCode: "" });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const tiers = info?.tiers || QTY_TIERS;
  const selectedCents = (tiers.find((t) => t.quantity === qty) || {}).cents || 3495;

  useEffect(() => { api.get("/cards/info").then((r) => setInfo(r.data)).catch(() => {}); }, []);

  const validate = () => {
    if (!f.firstName.trim()) return "Please enter your first name.";
    if (!f.lastName.trim()) return "Please enter your last name.";
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(f.email)) return "Please enter a valid email.";
    if (!f.addressLine1.trim()) return "Please enter your street address.";
    if (!f.city.trim()) return "Please enter your city.";
    if (!US_STATES.includes(f.state.trim().toUpperCase())) return "Please select a valid U.S. state.";
    if (!/^\d{5}(-\d{4})?$/.test(f.postCode.trim())) return "Please enter a valid U.S. ZIP code.";
    if (f.phone.replace(/\D/g, "").length < 10) return "Please enter a valid phone number for delivery.";
    return null;
  };

  const submit = async () => {
    const err = validate();
    if (err) { toast.error(err); return; }
    if (busy) return;
    setError("");
    setCheckoutUrl("");
    // The Emergent Preview embeds this app in an iframe, and Stripe Checkout refuses to render
    // inside a frame — that is what leaves the customer stuck on a blank skeleton. When we are
    // framed, open Stripe in a real top-level tab. We must open the blank tab synchronously here
    // (inside the click gesture) so it is not popup-blocked; its URL is set once checkout is ready.
    const inFrame = window.self !== window.top;
    let newTab = null;
    if (inFrame) { try { newTab = window.open("about:blank", "_blank"); } catch (_) {} }
    setBusy(true);
    const payload = {
      customer: { firstName: f.firstName.trim(), lastName: f.lastName.trim(), email: f.email.trim(), phone: f.phone.trim() },
      shipping: { addressLine1: f.addressLine1.trim(), addressLine2: f.addressLine2.trim(), city: f.city.trim(),
                  state: f.state.trim().toUpperCase(), postCode: f.postCode.trim(), country: "US" },
      quantity: qty,
      organization: f.organization.trim(),
      origin_url: window.location.origin,
    };
    try {
      const r = await api.post("/cards/checkout", payload, { timeout: 25000 });
      const url = r.data?.checkout_url;
      if (!url) throw new Error("no_url");
      if (!inFrame) { window.location.href = url; return; }  // direct/production: navigate in place
      if (newTab && !newTab.closed) { try { newTab.location.href = url; } catch (_) {} }
      // Show a persistent, clickable fallback so the customer is never stuck if the tab was blocked.
      setCheckoutUrl(url);
      setBusy(false);
    } catch (e) {
      if (newTab && !newTab.closed) { try { newTab.close(); } catch (_) {} }
      setBusy(false);
      const detail = e?.response?.data?.detail;
      setError(e?.code === "ECONNABORTED"
        ? "This is taking longer than usual. Please try again."
        : (typeof detail === "string" && detail) || "We couldn't start secure checkout. Please try again.");
    }
  };

  const field = (label, key, props = {}) => (
    <label className="block">
      <span className="text-xs font-semibold text-[#475569]">{label}</span>
      <input data-testid={`card-field-${key}`} value={f[key]} onChange={set(key)} {...props}
        className="mt-1 w-full rounded-lg border border-[#E2E8F0] px-3 py-2.5 text-sm focus:border-[#2C4C3B] outline-none" />
    </label>
  );

  return (
    <div className="max-w-5xl mx-auto px-5 sm:px-8 py-12">
      {info?.test_mode && (
        <div data-testid="cards-test-banner" className="mb-6 rounded-xl bg-[#FEF3C7] border border-[#FCD34D] px-4 py-3 text-sm text-[#92400E]">
          <strong>TEST MODE</strong> — this is a sandbox order flow. No cards will be manufactured or shipped, and no live charge is made.
        </div>
      )}
      <div className="grid md:grid-cols-2 gap-10">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-[#B8912F] font-semibold mb-2">Help share the wisdom</p>
          <h1 className="font-serif-display text-3xl sm:text-4xl leading-[1.1] text-[#0F172A]">Share Wisdom Wherever You Go</h1>
          <p className="mt-3 text-lg text-[#475569] leading-relaxed">A simple card can help someone find the Scripture they need at the moment they need it.</p>
          <div className="mt-6"><CardShowcase /></div>
          <p className="mt-6 text-[#475569] leading-relaxed">
            Keep them in your wallet, purse, car, office, church, or small group. When someone tells you what they're going through, hand them a card and give them an easy way to find Biblical wisdom for their situation.
          </p>

          <div className="mt-7 rounded-2xl border border-[#E7DFC9] bg-[#FBF7EE] px-6 py-5">
            <p className="text-xs uppercase tracking-[0.2em] text-[#B8912F] font-semibold mb-3">Why people carry them</p>
            <div className="space-y-2.5">
              {WHY_CARRY.map((t) => (
                <div key={t} className="flex items-start gap-2.5">
                  <span className="mt-0.5 grid place-items-center w-5 h-5 rounded-full bg-[#EAF1EA] shrink-0"><Check className="w-3.5 h-3.5 text-[#2C4C3B]" strokeWidth={2.5} /></span>
                  <span className="text-sm text-[#475569]">{t}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-6 flex items-center gap-3 text-[#0F172A]">
            <Truck className="w-5 h-5 text-[#2C4C3B]" />
            <span className="font-serif-display text-2xl">{qty} Sharing Cards — {priceStr(selectedCents)} Delivered</span>
          </div>
        </div>

        <div className="rounded-2xl border border-[#E2E8F0] bg-white p-6">
          <h2 className="font-serif-display text-xl text-[#0F172A] mb-4">Choose quantity</h2>
          <div data-testid="qty-selector" className="grid grid-cols-3 gap-3 mb-6">
            {tiers.map((t) => (
              <button key={t.quantity} type="button" data-testid={`qty-option-${t.quantity}`} onClick={() => setQty(t.quantity)}
                className={`rounded-xl border px-3 py-4 text-center transition-colors ${qty === t.quantity ? "border-[#2C4C3B] bg-[#EAF1EA] ring-1 ring-[#2C4C3B]" : "border-[#E2E8F0] bg-white hover:border-[#2C4C3B]/50"}`}>
                <span className="block font-serif-display text-xl text-[#0F172A]">{t.quantity}</span>
                <span className="block text-xs text-[#475569] mb-1">cards</span>
                <span className="block text-sm font-semibold text-[#2C4C3B]">{priceStr(t.cents)}</span>
              </button>
            ))}
          </div>
          <p className="text-xs text-[#94A3B8] mb-4">All quantities delivered. Same approved two-sided design. Larger or custom-branded orders? <a target="_top" href="mailto:wisdom@ineedaproverb.com?subject=Larger%20Sharing%20Card%20Order" className="font-semibold text-[#2C4C3B] underline underline-offset-2">Email us</a>.</p>
          <h2 className="font-serif-display text-xl text-[#0F172A] mb-4">Shipping details</h2>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              {field("First name", "firstName", { placeholder: "Jane" })}
              {field("Last name", "lastName", { placeholder: "Smith" })}
            </div>
            {field("Email", "email", { placeholder: "jane@example.com", type: "email" })}
            {field("Organization or church (optional)", "organization", { placeholder: "e.g. Grace Community Church" })}
            {field("Street address", "addressLine1", { placeholder: "123 Main St" })}
            {field("Apartment, suite (optional)", "addressLine2", { placeholder: "Apt 4B" })}
            {field("City", "city", { placeholder: "Springfield" })}
            <div className="grid grid-cols-2 gap-3">
              <label className="block">
                <span className="text-xs font-semibold text-[#475569]">State</span>
                <select data-testid="card-field-state" value={f.state} onChange={set("state")}
                  className="mt-1 w-full rounded-lg border border-[#E2E8F0] px-3 py-2.5 text-sm focus:border-[#2C4C3B] outline-none bg-white">
                  <option value="">State</option>
                  {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </label>
              {field("ZIP", "postCode", { placeholder: "62704" })}
            </div>
            {field("Phone (required for delivery)", "phone", { placeholder: "(555) 555-5555", type: "tel" })}
          </div>
          <button data-testid="card-order-submit" onClick={submit} disabled={busy || !!checkoutUrl}
            className="mt-6 w-full rounded-full bg-[#2C4C3B] text-white py-3.5 font-semibold hover:bg-[#24402F] transition-colors disabled:opacity-60 flex items-center justify-center gap-2">
            {busy && <Loader2 className="w-4 h-4 animate-spin" />}
            {busy ? "Starting secure checkout…" : `Order My ${qty} Sharing Cards — ${priceStr(selectedCents)}`}
          </button>

          {error && (
            <div data-testid="card-order-error" className="mt-4 rounded-xl border border-[#FCA5A5] bg-[#FEF2F2] px-4 py-3">
              <p className="text-sm text-[#B91C1C]">{error}</p>
              <button data-testid="card-order-retry" onClick={submit}
                className="mt-2 inline-flex items-center gap-2 rounded-full bg-[#B91C1C] text-white px-4 py-2 text-sm font-semibold hover:bg-[#991B1B] transition-colors">
                <RotateCcw className="w-4 h-4" /> Try again
              </button>
            </div>
          )}

          {checkoutUrl && (
            <div data-testid="card-order-continue" className="mt-4 rounded-xl border border-[#BBE0C6] bg-[#F0FAF3] px-4 py-3">
              <p className="text-sm text-[#166534]">Secure checkout opened in a new tab. If you don't see it, continue below.</p>
              <a data-testid="card-order-continue-link" href={checkoutUrl} target="_blank" rel="noopener noreferrer"
                className="mt-2 inline-flex items-center gap-2 rounded-full bg-[#2C4C3B] text-white px-4 py-2 text-sm font-semibold hover:bg-[#24402F] transition-colors">
                Continue to secure checkout →
              </a>
            </div>
          )}
          <p className="text-xs text-[#94A3B8] mt-3 text-center">Secure checkout via Stripe. U.S. shipping only.</p>
        </div>
      </div>
    </div>
  );
}

export function ShareCardsSuccess() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const sid = params.get("session_id");
  const [order, setOrder] = useState(null);
  const [tries, setTries] = useState(0);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    if (!sid) { nav("/cards"); return; }
    let stop = false;
    const poll = async () => {
      try {
        const r = await api.get(`/cards/order/${sid}`, { timeout: 15000 });
        if (stop) return;
        setOrder(r.data);
        setLoadError(false);
        if (r.data.payment_status !== "paid" && tries < 8) { setTimeout(() => setTries((t) => t + 1), 1500); }
      } catch {
        if (stop) return;
        if (tries < 8) setTimeout(() => setTries((t) => t + 1), 1500);
        else if (!order) setLoadError(true);
      }
    };
    poll();
    return () => { stop = true; };
  }, [sid, tries, nav]);

  const paid = order?.payment_status === "paid";
  const confirming = !paid && tries < 8;
  const retry = () => { setLoadError(false); setTries(0); };
  const ship = order?.shipping || {};
  const addr = [
    ship.addressLine1,
    ship.addressLine2,
    [ship.city, ship.state].filter(Boolean).join(", "),
    ship.postCode,
  ].filter((x) => x && String(x).trim());

  return (
    <div className="max-w-xl mx-auto px-5 sm:px-8 py-16 sm:py-20">
      {loadError && !order && (
        <div data-testid="success-load-error" className="rounded-2xl border border-[#FCA5A5] bg-[#FEF2F2] px-6 py-6 text-center">
          <p className="text-[#B91C1C] font-semibold">We couldn't load your order details.</p>
          <p className="mt-1 text-sm text-[#7F1D1D]">Your payment may still have gone through — we'll email you a confirmation. You can also retry below.</p>
          <button data-testid="success-retry" onClick={retry}
            className="mt-4 inline-flex items-center gap-2 rounded-full bg-[#B91C1C] text-white px-5 py-2.5 text-sm font-semibold hover:bg-[#991B1B] transition-colors">
            <RotateCcw className="w-4 h-4" /> Try again
          </button>
        </div>
      )}
      {!(loadError && !order) && (<>
      {order?.test_mode && (
        <div data-testid="success-test-badge" className="mb-6 text-center">
          <span className="inline-block rounded-full bg-[#FEF3C7] border border-[#FCD34D] px-4 py-1.5 text-xs font-semibold text-[#92400E]">TEST ORDER — no cards manufactured or shipped</span>
        </div>
      )}
      <div className="text-center">
        <div className="mx-auto grid place-items-center w-14 h-14 rounded-full bg-[#EAF1EA] mb-5"><Check className="w-7 h-7 text-[#2C4C3B]" strokeWidth={2.5} /></div>
        <h1 data-testid="cards-success-heading" className="font-serif-display text-3xl sm:text-4xl text-[#0F172A]">Your Sharing Cards Order Is Confirmed</h1>
        <p className="mt-4 text-[#475569] leading-relaxed">Thank you for helping share biblical wisdom with others. We'll email you when your cards ship.</p>
      </div>

      <div data-testid="order-summary" className="mt-8 rounded-2xl border border-[#E7DFC9] bg-white p-6 text-left">
        <div className="flex items-center justify-between pb-4 border-b border-[#F0EAdd]">
          <div>
            <p data-testid="order-product" className="font-serif-display text-lg text-[#0F172A]">{order?.product_name || "Sharing Cards"}</p>
            <p className="text-sm text-[#94A3B8]">Premium glossy · shipping included</p>
          </div>
          <p data-testid="order-amount" className="font-serif-display text-2xl text-[#0F172A]">{order?.amount_display || "—"}</p>
        </div>
        <dl className="mt-4 space-y-3 text-sm">
          <Row label="Order number" value={order?.order_reference_id || "—"} testid="order-reference" />
          <Row label="Email" value={order?.customer_email || "—"} testid="order-email" />
          <div className="flex justify-between gap-6">
            <dt className="text-[#94A3B8] shrink-0">Ship to</dt>
            <dd data-testid="order-shipping" className="text-[#0F172A] font-medium text-right">
              {order?.customer_name ? <div>{order.customer_name}</div> : null}
              {addr.length ? addr.map((line, i) => <div key={i}>{line}</div>) : <span className="text-[#94A3B8]">—</span>}
            </dd>
          </div>
          <Row label="Status" value={paid ? "Payment received" : (confirming ? "Confirming payment…" : "Awaiting payment confirmation")} testid="order-status" />
        </dl>
        {!paid && !confirming && (
          <button data-testid="success-refresh" onClick={retry}
            className="mt-4 inline-flex items-center gap-2 rounded-full border border-[#2C4C3B] text-[#2C4C3B] px-4 py-2 text-sm font-semibold hover:bg-[#F1F5F1] transition-colors">
            <RotateCcw className="w-4 h-4" /> Refresh status
          </button>
        )}
        <p className="mt-5 flex items-center gap-2 text-sm text-[#2C4C3B] border-t border-[#F0EAdd] pt-4">
          <Truck className="w-4 h-4" /> We'll email you when your cards ship.
        </p>
      </div>

      <div className="mt-8 text-center">
        <button onClick={() => nav("/")} className="rounded-full border border-[#2C4C3B] text-[#2C4C3B] px-6 py-2.5 font-semibold hover:bg-[#F1F5F1] transition-colors">Back to home</button>
      </div>
      </>)}
    </div>
  );
}

function Row({ label, value, testid }) {
  return (
    <div className="flex justify-between gap-6">
      <dt className="text-[#94A3B8] shrink-0">{label}</dt>
      <dd data-testid={testid} className="text-[#0F172A] font-medium text-right break-all">{value}</dd>
    </div>
  );
}
